/* eslint-disable complexity */
import _, { isEmpty } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { Avatar, Button, Text } from 'src/@vymo/ui/atoms';
import { List, Popup } from 'src/@vymo/ui/blocks';
import { ReactComponent as AddIcon } from 'src/assets/icons/addBoxLine.svg';
import { ReactComponent as CopilotIcon } from 'src/assets/icons/copilot_icon.svg';
import { ReactComponent as GridIcon } from 'src/assets/icons/gridFill.svg';
import { ReactComponent as NotificationIcon } from 'src/assets/icons/notificationLine.svg';
import { ReactComponent as SettingIcon } from 'src/assets/icons/settingLine.svg';
import { ReactComponent as SupersetIcon } from 'src/assets/icons/superset.svg';
import { ReactComponent as VymoLogo } from 'src/assets/images/vymoLogo.svg';
import { getGrowthBookFeatureFlag } from 'src/featureFlags';
import { Keys, locale } from 'src/i18n';
import { toggleCopilot } from 'src/models/appConfig/slice';
import {
  getLmsConfig,
  getLmsSessionIdentityKey,
} from 'src/models/auth/lmsLogin/selector';
import {
  getFeaturesConfig,
  getNonHierarchyRevyreModules,
} from 'src/models/lmsConfig/queries';
import { getPendingActivities } from 'src/models/lmsConfig/selectors';
import { REVYRE_FORM, SPG_WIDGET } from 'src/modules/constants';
// eslint-disable-next-line vymo-ui/restrict-import
import { REVYRE } from 'src/modules/revyre/constants';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import ChildComponent from 'src/workspace/childComponent';
import { renderDialog, renderModule } from 'src/workspace/slice';
import { getClientConfigData } from 'src/workspace/utils';
import { spgWidgetCodes } from './constant';
import Logout from './logout';
import NotificationsAndSuggestions from './notificationSugesstion';
import { globalActions } from './queries';
import SearchBar from './search';
import { getSuperSetDetails } from './selector';
import { superSetLogin } from './thunk';
import { SupersetDetails } from './types';
import styles from './index.module.scss';

function Navbar() {
  const lmsSessionIdentityKey = useAppSelector(getLmsSessionIdentityKey);

  const lmsConfig = useAppSelector(getLmsConfig);
  const workspaceSessionConfig = useMemo(() => {
    if (lmsConfig?.client || lmsConfig?.user?.code) {
      return lmsConfig;
    }
    return getClientConfigData() ?? {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lmsConfig, lmsSessionIdentityKey]);
  const dispatch = useAppDispatch();
  const superSetDetails = useAppSelector((state) =>
    getSuperSetDetails(state),
  ) as SupersetDetails;
  const featuresConfig = getFeaturesConfig();
  const branding = _.get(workspaceSessionConfig, 'branding');
  const minCharRequired =
    _.get(workspaceSessionConfig, 'search_min_required_chars') || 3;
  const user = workspaceSessionConfig?.user ?? {};
  const showMenu = _.get(user, 'ssAccess', false);
  const isSpgEnabled = getGrowthBookFeatureFlag('enable-spg');
  const isCopilotEnabled = getGrowthBookFeatureFlag('enable-copilot-platform');
  const pendingActivities = useAppSelector((state) =>
    getPendingActivities(state),
  );
  const firstPendingActivity = pendingActivities?.[0]?.mutatedEntity;
  useEffect(() => {
    if (firstPendingActivity?.id) {
      dispatch(
        renderModule({
          id: 'REVYRE_DETAILS',
          props: {
            domainType: 'activity',
            workflowType: firstPendingActivity?.workflowType,
            entityId: firstPendingActivity?.id,
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, JSON.stringify(firstPendingActivity)]);

  const isNudgeDropDown = _.get(
    workspaceSessionConfig,
    'suggestions_config.isNudgesDropdownEnabledForWeb',
  );
  const isSuperSetEnable =
    _.get(featuresConfig, 'superset_enabled', false) && showMenu;

  const userRole = user.roles?.[0] || user.attributes?.role;

  useEffect(() => {
    if (isSuperSetEnable && userRole && isEmpty(superSetDetails)) {
      dispatch(
        superSetLogin({
          params: { user_role: userRole },
        }),
      );
    }
  }, [isSuperSetEnable, dispatch, userRole, superSetDetails]);

  const getBrandingLogo = () => {
    if (branding) {
      const { logo, default_logo: defaultLogo } = branding;
      if (logo) {
        return logo;
      }
      if (defaultLogo && defaultLogo?.url && defaultLogo?.url?.length) {
        return defaultLogo.url;
      }
    }
    return '';
  };

  const onQuickActionClick = (actionCode) => {
    const revyreGlobalWorkflowModulesCodes = getNonHierarchyRevyreModules()
      .filter(
        (module) =>
          !REVYRE.COLLECT_IQ.CREATE_SCENARIO_BLOCKED_ENTITIES.includes(
            module?.code,
          ),
      )
      .map((module) => module?.code);

    if (!revyreGlobalWorkflowModulesCodes.includes(actionCode)) {
      dispatch(
        renderDialog({
          id: 'WORKFLOW_FORM',
          props: {
            action: actionCode,
            voId: 'global',
            moduleCode: 'global',
          },
        }),
      );
    }

    switch (actionCode) {
      case 'batch':
        dispatch(
          renderDialog({
            id: REVYRE_FORM,
            props: {
              type: 'create',
              domainType: 'workflow',
              workflowType: 'batch',
            },
          }),
        );
        break;

      case 'payment':
        dispatch(
          renderDialog({
            id: REVYRE_FORM,
            props: {
              type: 'create',
              domainType: 'payment',
            },
          }),
        );
        break;

      case 'activity':
        dispatch(
          renderDialog({
            id: REVYRE_FORM,
            props: {
              type: 'create',
              domainType: 'activity',
            },
          }),
        );
        break;

      default:
        break;
    }
  };

  if (isEmpty(user) || !user) return null;

  return (
    <div className={styles.navbar} data-test-id="vymoWeb-header">
      <div className={styles.navbar__logo}>
        {getBrandingLogo() ? (
          <img src={getBrandingLogo()} height={40} alt="logo" />
        ) : (
          <VymoLogo />
        )}
      </div>
      <div className={styles.navbar__leftPane}>
        <SearchBar minCharsRequired={minCharRequired} />
        <div className={styles.navbar__toolsPane}>
          <div className={styles.navbar__toolsPane__tools}>
            <Popup
              data-test-id="quick-action-button"
              content={
                <List
                  onItemClick={(idx) =>
                    onQuickActionClick(globalActions()?.[idx]?.code)
                  }
                  className={styles.navbar__quickActionList}
                >
                  {globalActions()?.map((action) => (
                    <List.Item key={action?.code}>
                      <Text>{action?.name}</Text>
                    </List.Item>
                  ))}
                </List>
              }
            >
              <Button
                className={styles.navbar__toolsPane__tools__icon}
                iconProps={{ icon: <AddIcon />, iconPosition: 'left' }}
                type="text"
              />
            </Popup>
            {isSpgEnabled && spgWidgetCodes?.length && (
              <Popup
                content={
                  <div className={styles.globalSpg}>
                    {spgWidgetCodes.map((code) => (
                      <ChildComponent
                        key={code}
                        id={SPG_WIDGET}
                        props={{
                          locationCode: 'home',
                          widgetCode: code,
                        }}
                      />
                    ))}
                  </div>
                }
              >
                <Button
                  className={styles.navbar__toolsPane__tools__icon}
                  iconProps={{ icon: <GridIcon />, iconPosition: 'left' }}
                  type="text"
                />
              </Popup>
            )}
            {showMenu && (
              <Button
                className={styles.navbar__toolsPane__tools__icon}
                iconProps={{ icon: <SettingIcon />, iconPosition: 'left' }}
                type="link"
                variant="subtle"
                linkProps={{
                  href: '/v2/selfserve',
                  target: '_blank',
                  rel: 'noopener noreferrer',
                }}
                data-test-id="self-serve"
              />
            )}
            {Boolean(isSuperSetEnable && superSetDetails) && (
              <Button
                className={styles.navbar__toolsPane__tools__icon}
                iconProps={{ icon: <SupersetIcon />, iconPosition: 'left' }}
                type="link"
                variant="subtle"
                linkProps={{
                  href: `${superSetDetails?.baseUrl}/login?token=${superSetDetails?.token}`,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                }}
              />
            )}
            {isNudgeDropDown && (
              <Popup
                placement="bottom"
                className={styles.notificationPopup}
                content={<NotificationsAndSuggestions />}
              >
                <Button
                  className={styles.navbar__toolsPane__tools__icon}
                  iconProps={{
                    icon: <NotificationIcon />,
                    iconPosition: 'left',
                  }}
                  type="text"
                />
              </Popup>
            )}
          </div>
          {isCopilotEnabled && (
            <Button
              className={styles.navbar__copilotBtn}
              size="small"
              iconProps={{ iconPosition: 'left', icon: <CopilotIcon /> }}
              onClick={() => dispatch(toggleCopilot())}
            >
              {locale(Keys.ASK_COPILOT)}
            </Button>
          )}
          <Popup placement="bottom" content={<Logout />}>
            <Button type="text" size="small" isIcon>
              <Avatar
                text={(user?.name ?? '?').charAt(0)}
                imageUrl={user.profile_photo_s3_link}
                classNames={styles.navbar__logoutAvatar}
              />
            </Button>
          </Popup>
        </div>
      </div>
    </div>
  );
}

export default Navbar;
